import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, PaperPlaneTilt, Copy, Trash, EnvelopeSimple, House, Clock } from "@phosphor-icons/react";
import DraftUpdateModal from "@/components/DraftUpdateModal";
import { Link } from "react-router-dom";

export default function DraftsQueue() {
  const [drafts, setDrafts] = useState([]);
  const [tab, setTab] = useState("draft"); // draft | sent
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // draft object being edited via modal

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/updates/drafts?status=${tab}`);
      setDrafts(data || []);
    } catch (e) { toast.error("Failed to load drafts"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const quickSend = async (d) => {
    if (!d.landlord_email) { toast.error("No email on file — open draft to Copy & Send"); return; }
    try {
      await api.post(`/updates/drafts/${d.id}/send`);
      toast.success(`Sent to ${d.landlord_email}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed")); }
  };

  const quickCopy = async (d) => {
    try {
      await navigator.clipboard.writeText(`Subject: ${d.subject}\n\n${d.body}`);
      await api.post(`/updates/drafts/${d.id}/mark-sent`);
      toast.success("Copied · marked sent");
      load();
    } catch (e) { toast.error("Copy failed"); }
  };

  const discard = async (d) => {
    if (!window.confirm(`Discard draft "${d.subject}"?`)) return;
    try { await api.delete(`/updates/drafts/${d.id}`); toast.success("Discarded"); load(); }
    catch { toast.error("Failed"); }
  };

  const relTime = (iso) => {
    if (!iso) return "";
    try {
      const t = new Date(iso).getTime();
      const diff = Date.now() - t;
      const m = Math.floor(diff / 60000);
      if (m < 60) return `${m}m ago`;
      const h = Math.floor(m / 60); if (h < 48) return `${h}h ago`;
      return `${Math.floor(h / 24)}d ago`;
    } catch { return ""; }
  };

  return (
    <AdminShell>
      <div className="space-y-6" data-testid="drafts-queue-page">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
            <Sparkle weight="fill" className="text-fuchsia-500"/> Drafts Queue
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Every AI-drafted landlord update lands here — one place to review, edit, and ship
            status replies. The Monday cron auto-drafts updates for any open job untouched in 7+ days.
          </p>
        </div>

        <div className="flex gap-2">
          {[
            { key: "draft", label: `Pending Review (${drafts.length && tab === "draft" ? drafts.length : "…"})` },
            { key: "sent", label: "Sent" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`drafts-tab-${t.key}`}
              className={`h-9 px-4 font-black uppercase tracking-widest text-[11px] border-2 border-slate-900 ${
                tab === t.key ? "bg-slate-900 text-white shadow-[2px_2px_0_#0f172a]" : "bg-white text-slate-900 hover:bg-slate-100"
              }`}>{t.label}</button>
          ))}
        </div>

        {busy && <div className="text-sm text-slate-500">Loading…</div>}

        {!busy && drafts.length === 0 && (
          <div className="p-8 bg-emerald-50 border-4 border-emerald-500 text-center" data-testid="drafts-empty">
            <div className="text-3xl mb-2">🎉</div>
            <div className="font-black uppercase tracking-widest text-emerald-900">
              {tab === "draft" ? "Inbox zero — no drafts pending" : "No sent drafts yet"}
            </div>
            <div className="text-xs text-slate-600 mt-2">
              {tab === "draft" && "The Monday 6am cron will drop stale-job drafts here automatically."}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {drafts.map(d => (
            <div key={d.id} className="rugged-card p-4 space-y-3" data-testid={`draft-card-${d.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-black text-base line-clamp-2" data-testid={`draft-subject-${d.id}`}>{d.subject}</div>
                  <div className="text-xs text-slate-600 flex items-center gap-1 mt-1">
                    <EnvelopeSimple size={11}/>
                    <b>{d.landlord_name || "—"}</b>
                    {d.landlord_email && <span>&lt;{d.landlord_email}&gt;</span>}
                  </div>
                  {d.property_address && (
                    <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <House size={11}/> {d.property_address}
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  {d.source === "weekly_cron" && (
                    <span className="inline-block px-2 h-5 bg-fuchsia-100 text-fuchsia-700 font-black uppercase tracking-widest text-[9px] leading-5">
                      Auto
                    </span>
                  )}
                  <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-1"><Clock size={10}/>{relTime(d.created_at)}</div>
                </div>
              </div>

              {d.landlord_question && (
                <div className="p-2 bg-amber-50 border-l-4 border-amber-500 text-xs italic">
                  "{d.landlord_question.slice(0, 200)}{d.landlord_question.length > 200 ? "…" : ""}"
                </div>
              )}

              <div className="p-3 bg-slate-50 border-2 border-slate-200 text-xs whitespace-pre-wrap max-h-40 overflow-y-auto font-mono leading-relaxed" data-testid={`draft-preview-${d.id}`}>
                {d.body.slice(0, 500)}{d.body.length > 500 ? "\n…" : ""}
              </div>

              {tab === "draft" && (
                <div className="flex flex-wrap gap-2 pt-1 border-t-2 border-slate-100">
                  <button onClick={() => setEditing(d)} data-testid={`draft-edit-${d.id}`}
                    className="h-9 px-3 border-2 border-fuchsia-500 text-fuchsia-700 hover:bg-fuchsia-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                    Review & Edit
                  </button>
                  <button onClick={() => quickCopy(d)} data-testid={`draft-quick-copy-${d.id}`}
                    className="h-9 px-3 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                    <Copy size={11}/> Copy & Mark Sent
                  </button>
                  {d.landlord_email && (
                    <button onClick={() => quickSend(d)} data-testid={`draft-quick-send-${d.id}`}
                      className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 shadow-[2px_2px_0_#0f172a]">
                      <PaperPlaneTilt weight="fill" size={11}/> Send
                    </button>
                  )}
                  <button onClick={() => discard(d)} data-testid={`draft-discard-${d.id}`}
                    className="h-9 px-3 border-2 border-red-500 text-red-600 hover:bg-red-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 ml-auto">
                    <Trash size={11}/>
                  </button>
                </div>
              )}
              {tab === "sent" && d.sent_at && (
                <div className="text-[10px] text-emerald-700 font-black uppercase tracking-widest">
                  ✓ Sent via {d.sent_via || "email"} · {relTime(d.sent_at)}
                </div>
              )}
              {d.job_id && (
                <Link to={`/admin/jobs/${d.job_id}`} className="text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:underline">
                  View job →
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <ReviewEditModal
          draft={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}
    </AdminShell>
  );
}

/** Small wrapper to reuse DraftUpdateModal's editing UI without re-generating.
 *  We prime the modal with the existing draft. */
function ReviewEditModal({ draft, onClose, onDone }) {
  // DraftUpdateModal handles jobId/landlordId to CREATE a draft. For editing
  // an existing draft we skip generation entirely and open directly into the
  // edit view via inline state — same PATCH + send/copy endpoints.
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [sending, setSending] = useState(false);

  const save = async () => {
    if (subject === draft.subject && body === draft.body) return true;
    try { await api.patch(`/updates/drafts/${draft.id}`, { subject, body }); return true; }
    catch { toast.error("Save failed"); return false; }
  };
  const doSend = async () => {
    setSending(true);
    if (!(await save())) { setSending(false); return; }
    try { await api.post(`/updates/drafts/${draft.id}/send`); toast.success("Sent"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed")); }
    finally { setSending(false); }
  };
  const doCopy = async () => {
    setSending(true);
    if (!(await save())) { setSending(false); return; }
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      await api.post(`/updates/drafts/${draft.id}/mark-sent`);
      toast.success("Copied · marked sent"); onDone();
    } catch { toast.error("Copy failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="draft-review-modal">
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-4 bg-fuchsia-600 text-white flex items-start justify-between sticky top-0">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Review & Edit</div>
            <div className="text-lg font-black uppercase tracking-widest">{draft.landlord_name || "Landlord Update"}</div>
          </div>
          <button onClick={onClose} data-testid="draft-review-close" className="p-1 hover:bg-fuchsia-700">×</button>
        </div>
        <div className="p-4 space-y-3">
          <input value={subject} onChange={e => setSubject(e.target.value)} data-testid="review-subject"
            className="w-full h-10 px-3 border-2 border-slate-300 focus:border-fuchsia-500 outline-none text-sm font-medium"/>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={16} data-testid="review-body"
            className="w-full p-3 border-2 border-slate-300 focus:border-fuchsia-500 outline-none text-sm font-mono leading-relaxed"/>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={doCopy} disabled={sending} data-testid="review-copy"
              className="h-11 border-2 border-slate-900 font-black uppercase tracking-widest text-[11px] hover:bg-slate-100 disabled:opacity-50 inline-flex items-center justify-center gap-1">
              <Copy size={12}/> Copy & Mark Sent
            </button>
            <button onClick={doSend} disabled={sending || !draft.landlord_email} data-testid="review-send"
              className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[11px] disabled:opacity-40 shadow-[2px_2px_0_#0f172a] inline-flex items-center justify-center gap-1">
              <PaperPlaneTilt weight="fill" size={12}/> {sending ? "…" : "Send via Email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
